/*  @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import type { Profile } from '../../../helpers/profiles/typesAndConstants';
import type { CONFIRMATION_DATA_MODAL_TYPE } from '../confirmationModal/ConfirmationModal';

import { logError } from '../../../helpers/debug/debug';
import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import { isProfilePrivate } from '../../../helpers/profiles/helpers';
import ProfilesManager from '../../../helpers/profiles/profilesManager';
import { showConfirmationModal, showPinModal } from '../../../redux/modal/actions';
import RowSettingAction from '../../settings/RowSettingAction';
import RowSettingToggle from '../../settings/RowSettingToggle';
import { ConfirmationModalResult } from '../confirmationModal/ConfirmationModal';
import { PinModalKind } from '../pinModal/PinModalConstsAndTypes';
import './SharedProfileSettings.css';

type SharedProfileSettingsPropType = {
  +canChangeProfileType: boolean,
  +className?: string,
  +onProfileChange: (profile: Profile) => Promise<void> | void,
  +profile: Profile,
};

const SharedProfileSettings = ({ canChangeProfileType, className, onProfileChange, profile }: SharedProfileSettingsPropType): React.Node => {
  const dispatch = useDispatch();

  const isPrivate = isProfilePrivate(profile);

  const pinCreationClosedCallback = useCallback(
    (pinCode: string) => {
      if (pinCode === undefined) {
        // User cancled
        return;
      }

      const result = onProfileChange({ ...profile, isKid: false, pinCode });

      if (result instanceof Promise) {
        result.catch((error) => {
          logError(`Error in SharedProfileSettings pinCreationClosedCallback: ${error.message}`);
        });
      }
    },
    [onProfileChange, profile],
  );

  const profilePrivateToKidConfirmationClosedCallback = useCallback(
    (result: ConfirmationModalResult) => {
      if (result !== ConfirmationModalResult.Button2) {
        // Profile type switch canceled
        return;
      }

      onProfileChange({ ...profile, isKid: true, pinCode: undefined });
    },
    [onProfileChange, profile],
  );

  const handleKidProfileOnChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        if (isPrivate) {
          // Profile is currently private but admin wants to switch to kid
          const data: CONFIRMATION_DATA_MODAL_TYPE = {
            button1Title: Localizer.localize('common.actions.cancel'),
            button2Title: Localizer.localize('profiles.profile_editor.validate'),
            header: Localizer.localize('profiles.profile_editor.delete_profile_pin.title'),
            question: Localizer.localize('profiles.profile_editor.delete_profile_pin.question'),
          };

          const confirmationModalId = crypto.randomUUID();
          Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${confirmationModalId}`, profilePrivateToKidConfirmationClosedCallback);
          dispatch(showConfirmationModal(data, Localizer.localize('accessibility.modal.confirmation_delete_profile_pin'), confirmationModalId));
          return;
        }
      }

      onProfileChange({ ...profile, isKid: checked });
    },
    [isPrivate, onProfileChange, profile, profilePrivateToKidConfirmationClosedCallback],
  );

  const profilePinDeleteConfirmationClosedCallback = useCallback(
    (result: ConfirmationModalResult) => {
      if (result !== ConfirmationModalResult.Button2) {
        // Delete canceled
        return;
      }

      onProfileChange({ ...profile, pinCode: undefined });
    },
    [onProfileChange, profile],
  );

  const handlePrivateProfileOnChanged = useCallback(
    async (checked: boolean) => {
      if (checked) {
        // User enabled private profile: show PIN modal to create a new PIN code
        const pinCreationModalId = crypto.randomUUID();
        Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${pinCreationModalId}`, pinCreationClosedCallback);
        dispatch(showPinModal({ kind: PinModalKind.CreateProfile }, pinCreationModalId));
      } else {
        // User disabled private profile: show confirmation modal before actually going public
        const data: CONFIRMATION_DATA_MODAL_TYPE = {
          button1Title: Localizer.localize('common.actions.cancel'),
          button2Title: Localizer.localize('profiles.profile_editor.validate'),
          header: Localizer.localize('profiles.profile_editor.delete_profile_pin.title'),
          question: Localizer.localize('profiles.profile_editor.delete_profile_pin.question'),
        };

        const confirmationModalId = crypto.randomUUID();
        Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${confirmationModalId}`, profilePinDeleteConfirmationClosedCallback);
        dispatch(showConfirmationModal(data, Localizer.localize('accessibility.modal.confirmation_delete_profile_pin'), confirmationModalId));
      }
    },
    [pinCreationClosedCallback, profilePinDeleteConfirmationClosedCallback],
  );

  const pinChangeClosedCallback = useCallback(
    (pinCode?: string) => {
      if (typeof pinCode !== 'string') {
        // User closed PIN change modal without saving new code
        return;
      }

      onProfileChange({ ...profile, pinCode });
    },
    [onProfileChange, profile],
  );

  const handleChangePinOnClick = useCallback(() => {
    Messenger.once(MessengerEvents.MODAL_CLOSED, pinChangeClosedCallback);
    dispatch(showPinModal({ kind: PinModalKind.Modify }));
  }, [pinChangeClosedCallback]);

  return (
    <div className={clsx('sharedProfileSettings', className)}>
      {canChangeProfileType && ProfilesManager.isKidProfileAvailable() ? (
        <RowSettingToggle checked={profile.isKid === true} onChange={handleKidProfileOnChange}>
          <div>{Localizer.localize('profiles.profile_editor.kid_profile')}</div>
        </RowSettingToggle>
      ) : null}
      {canChangeProfileType || profile.isKid !== true ? (
        <RowSettingToggle checked={isPrivate} className='sharedProfileSettingsRow' onChange={handlePrivateProfileOnChanged}>
          <div className='sharedProfileSettingsLabelColumn'>
            <div>{Localizer.localize('profiles.profile_editor.privacy.private_profile')}</div>
            <div className='sharedProfileSettingsHint'>{Localizer.localize('profiles.profile_editor.privacy.hint')}</div>
          </div>
        </RowSettingToggle>
      ) : null}
      {isPrivate ? (
        <RowSettingAction aria-label={Localizer.localize('accessibility.profiles.modify_pin', { name: profile.name })} className='sharedProfileSettingsRow' onClick={handleChangePinOnClick}>
          <div>{Localizer.localize('profiles.profile_editor.privacy.change_profile_pin')}</div>
        </RowSettingAction>
      ) : null}
    </div>
  );
};

export default SharedProfileSettings;
